import { FC } from "react";
import { CustomCellRendererProps } from "ag-grid-react";
import Stack from "@mui/material/Stack";
import { SavingsOpportunity } from "@/services/cloudchipr.api";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { CopyIconButton } from "@/components/common/CopyIconButton";
import { useHover } from "@/utils/hooks/useHover.hook";
import { underutilizedSavingOpportunitiesDescription } from "@/components/pages/savings-opportunities/utils/constants/common";
import { kubernetesResourceTypes } from "@/store/resources-grid/utils/constants/common";
import { AiKubernetesRecommendationDescriptionTooltip } from "@/components/pages/ai-kubernetes-recommendations/components/AiKubernetesRecommendationDescriptionTooltip";
import { getTextFromHTML } from "@/components/utils/helpers/slack-formatters/getTextFromHTML";

export const SavingsOpportunityDescriptionCellRenderer: FC<
  CustomCellRendererProps<SavingsOpportunity>
> = ({ data }) => {
  const { ref, hovered } = useHover();
  const isKubernetes =
    !!data?.service && kubernetesResourceTypes.has(data?.service);

  let description = data?.description ?? "";

  if (data?.action_type === "live_filtered_resource") {
    description = underutilizedSavingOpportunitiesDescription;
  }

  if (isKubernetes) {
    description = getTextFromHTML(description);
  }

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" ref={ref}>
      <TypographyWithTooltip
        title={
          isKubernetes ? (
            <AiKubernetesRecommendationDescriptionTooltip
              description={data?.description ?? ""}
            />
          ) : (
            description
          )
        }
        variant="body2"
        tooltipMaxHeight="38dvh"
        placement={isKubernetes ? "right-start" : "bottom"}
        tooltipProps={{
          arrow: !isKubernetes,
          componentsProps: isKubernetes ? customTooltipStyles : undefined,
        }}
      >
        {description}
      </TypographyWithTooltip>

      {description && (
        <CopyIconButton
          data-block-row-click
          data={description}
          visible={hovered}
          TooltipProps={{ placement: "top" }}
        />
      )}
    </Stack>
  );
};

const customTooltipStyles = {
  tooltip: {
    sx: {
      color: "text.primary",
      bgcolor: "background.paper",
      "& .MuiTooltip-arrow": {
        color: "background.paper",
      },
      boxShadow: 7,
      border: 1,
      borderColor: "grey.300",
      borderRadius: 3,
      p: 1,
      maxWidth: "55dvh",
      maxHeight: "40dvh",
      overflow: "hidden",
    },
  },
};
